/**
 * BarDrilldownWidget — a Highcharts column-drilldown chart with optional
 * metadata chrome (headline stat, status banner, technical detail glossary).
 *
 * chart_type: "bar_drilldown" — NOT "bar". "bar" is already registered
 * (BarRenderer.ts) for a different, flat non-drilldown shape; reusing it
 * would silently change that renderer's behavior for every widget already
 * using it. Matches the existing pie_drilldown/grouped_bar convention of
 * giving a shape variant its own distinct chart_type.
 *
 * Highcharts' drilldown module is already globally registered
 * (ChartFactory.tsx) and natively understands series[].data[].drilldown →
 * drilldown.series[].id — click/breadcrumb/back-button is entirely built
 * in, no custom React state needed for the chart itself.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import { toSafeNumber } from "src/charts/data/transformers";

type BarPoint = {
  x?: string;
  name?: string;
  y?: unknown;
  drilldown?: string;
};

type BarSeries = {
  name?: string;
  type?: string;
  data?: BarPoint[];
};

type DrilldownSeries = {
  id?: string;
  name?: string;
  type?: string;
  data?: (BarPoint | unknown[])[];
};

type Headline = { label?: string; value?: string; subtitle?: string };
type Status = { code?: string; label?: string; description?: string };
type TechnicalDetailRow = {
  measure?: string;
  cases?: number;
  definition?: string;
};

type Metadata = {
  headline?: Headline;
  status?: Status;
  technical_detail_rows?: TechnicalDetailRow[];
};

type BarDrilldownData = {
  series?: BarSeries[];
  drilldown?: { series?: DrilldownSeries[] };
};

type BarDrilldownWidgetProps = {
  isLoading: boolean;
  data: unknown;
};

// Sequential single hue for the operational bars — this is a magnitude
// comparison (one series per drill level), not categorical identity, so one
// consistent color throughout the whole drill hierarchy is correct; depth is
// communicated by Highcharts' own breadcrumb, not by color.
const PRIMARY_COLOR = "#2a78d6";
// Neutral gray for "denominator"/context bars (e.g. "Total Cases") — these
// aren't an operational metric among peers, so they're deliberately
// desaturated rather than given a categorical hue. Validated: CVD ΔE 16.3,
// normal-vision ΔE 18.6 vs the primary blue (scripts/validate_palette.js).
const CONTEXT_COLOR = "#94a3b8";

// Fixed status palette (validated: good/critical clear 3:1 on the light
// surface; warning/serious don't — both always ship with an icon + label
// below, never color alone, per the relief rule).
const STATUS_STYLES: Record<string, { color: string; icon: string }> = {
  good: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  ok: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  healthy: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  warning: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  no_new_data: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  serious: { color: "#ec835a", icon: "bi-exclamation-circle-fill" },
  degraded: { color: "#ec835a", icon: "bi-exclamation-circle-fill" },
  critical: { color: "#d03b3b", icon: "bi-x-circle-fill" },
  error: { color: "#d03b3b", icon: "bi-x-circle-fill" },
};
const DEFAULT_STATUS_STYLE = STATUS_STYLES.warning!;

const unwrap = (
  raw: unknown,
): { data?: BarDrilldownData; metadata?: Metadata } => {
  if (!raw || typeof raw !== "object") return {};
  return raw as { data?: BarDrilldownData; metadata?: Metadata };
};

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const formatNum = (val: unknown): string =>
  isNumeric(val) ? val.toLocaleString() : String(val ?? "–");

// A bar reads as "context" (the whole, not a peer metric) when its label
// starts with "Total" — styled in neutral gray instead of the primary hue.
// A prefix match, not a hardcoded label, so this holds for any bar_drilldown
// payload that includes a totals bar, not just this one dataset.
const isContextPoint = (name: string) => /^total\b/i.test(name.trim());

function buildOptions(data: BarDrilldownData | undefined): Highcharts.Options {
  const drilldownSeriesList = data?.drilldown?.series ?? [];
  const findSeries = (id?: string) =>
    id ? drilldownSeriesList.find((series) => series.id === id) : undefined;

  const topSeries = data?.series?.[0];
  const topData = (topSeries?.data ?? []).map((point) => {
    const name = String(point.x ?? point.name ?? "");
    // Highcharts' drilldown module treats any `drilldown` id as clickable —
    // only attach it when that id's series genuinely has data, so a 0-count
    // bar (e.g. "Denial Task": 0) reads as a dead end instead of inviting a
    // click into an empty chart.
    const target = findSeries(point.drilldown);
    const hasDrilldown = !!target && (target.data?.length ?? 0) > 0;
    return {
      name,
      y: toSafeNumber(point.y),
      color: isContextPoint(name) ? CONTEXT_COLOR : PRIMARY_COLOR,
      ...(hasDrilldown ? { drilldown: point.drilldown } : {}),
    };
  });

  const drilldownSeries = drilldownSeriesList
    .filter((series) => (series.data?.length ?? 0) > 0)
    .map((series) => {
      const id = String(series.id ?? series.name ?? "");
      return {
        id,
        name: series.name ?? id,
        type: "column" as const,
        color: PRIMARY_COLOR,
        data: (series.data ?? []).map((point) => {
          if (Array.isArray(point)) {
            return { name: String(point[0] ?? ""), y: toSafeNumber(point[1]) };
          }
          const p = point as BarPoint;
          // Same rule one level deeper (e.g. PA Denied → PA Denied Reasons).
          const next = findSeries(p.drilldown);
          const hasNext = !!next && (next.data?.length ?? 0) > 0;
          return {
            name: String(p.x ?? p.name ?? ""),
            y: toSafeNumber(p.y),
            ...(hasNext ? { drilldown: p.drilldown } : {}),
          };
        }),
      };
    });

  return {
    chart: { type: "column", style: { fontFamily: "inherit" } },
    title: { text: "" },
    xAxis: { type: "category", crosshair: true },
    yAxis: { title: { text: "" }, gridLineColor: "#eceae4" },
    legend: { enabled: false },
    credits: { enabled: false },
    tooltip: {
      headerFormat: "<b>{point.key}</b><br/>",
      pointFormat: "{series.name}: <b>{point.y:,.0f}</b>",
    },
    plotOptions: {
      column: {
        // A 0-value bar still gets a hairline sliver so it never visually
        // vanishes next to a much larger bar in the same series — the data
        // label carries the exact value regardless of bar height.
        minPointLength: 3,
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: "{point.y:,.0f}",
          style: { fontWeight: "600", textOutline: "none" },
        },
      },
    },
    series: [
      {
        type: "column",
        name: topSeries?.name ?? "",
        data: topData,
      },
    ],
    drilldown: {
      activeAxisLabelStyle: { color: "#0b0b0b", fontWeight: "600" },
      activeDataLabelStyle: { color: "#0b0b0b", fontWeight: "600" },
      series: drilldownSeries,
      breadcrumbs: { showFullPath: true },
    },
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      // Same offset every other chart config in this app uses (barChartConfig,
      // pieDrilldownChartConfig, etc.) to tuck Highcharts' own context button
      // out of view — the card shell renders the one visible hamburger and
      // proxies its click into this button (WidgetRenderer.tsx's
      // openExportingMenu), so without this offset both buttons show at once.
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -30,
        },
      },
    },
  };
}

export default function BarDrilldownWidget({
  isLoading,
  data,
}: BarDrilldownWidgetProps) {
  const { data: chartData, metadata } = useMemo(() => unwrap(data), [data]);
  const options = useMemo(() => buildOptions(chartData), [chartData]);
  const [showDetails, setShowDetails] = useState(false);

  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // Highcharts sizes itself to its container once at mount and has no way to
  // know the container's height changed afterward without a browser resize
  // event — toggling the technical-details table below it shrinks/grows this
  // same flex column, so without this the chart collapses when details open
  // and never recovers when they close. Same ResizeObserver → reflow() fix
  // ChartFactory.tsx already uses for fillHeight charts.
  useEffect(() => {
    const reflow = () => chartRef.current?.chart?.reflow();
    const timer = setTimeout(reflow, 0);
    const el = chartWrapperRef.current;
    if (!el) return () => clearTimeout(timer);
    const observer = new ResizeObserver(reflow);
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [showDetails]);

  if (isLoading) {
    return (
      <div className="placeholder-glow h-100 w-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const hasChartData = (chartData?.series?.[0]?.data?.length ?? 0) > 0;
  const headline = metadata?.headline;
  const status = metadata?.status;
  const statusStyle = status?.code
    ? (STATUS_STYLES[status.code] ?? DEFAULT_STATUS_STYLE)
    : null;
  const detailRows = metadata?.technical_detail_rows ?? [];

  return (
    <div className="bar-drilldown">
      {headline && (
        <div className="bar-drilldown__headline">
          {headline.label && (
            <span className="bar-drilldown__headline-label">
              {headline.label}
            </span>
          )}
          {headline.value && (
            <span className="bar-drilldown__headline-value">
              {headline.value}
            </span>
          )}
          {headline.subtitle && (
            <span className="bar-drilldown__headline-subtitle">
              {headline.subtitle}
            </span>
          )}
        </div>
      )}

      {status && statusStyle && (
        <div className="bar-drilldown__status" role="status">
          <i
            className={`bi ${statusStyle.icon}`}
            style={{ color: statusStyle.color }}
            aria-hidden="true"
          />
          <div className="bar-drilldown__status-text">
            {status.label && (
              <span className="bar-drilldown__status-label">
                {status.label}
              </span>
            )}
            {status.description && (
              <span className="bar-drilldown__status-desc">
                {status.description}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="bar-drilldown__chart" ref={chartWrapperRef}>
        {hasChartData ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            ref={chartRef}
          />
        ) : (
          <p className="text-muted small mb-0">No data available.</p>
        )}
      </div>

      {detailRows.length > 0 && (
        <div className="bar-drilldown__details">
          <button
            type="button"
            className="bar-drilldown__details-toggle"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((value) => !value)}
          >
            <i
              className={`bi bi-chevron-${showDetails ? "down" : "right"}`}
              aria-hidden="true"
            />
            <span>Technical details</span>
          </button>

          {showDetails && (
            <table className="bar-drilldown__details-table">
              <thead>
                <tr>
                  <th>Measure</th>
                  <th>Cases</th>
                  <th>Definition</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row, index) => (
                  <tr key={row.measure ?? index}>
                    <td>{row.measure ?? "—"}</td>
                    <td>{formatNum(row.cases)}</td>
                    <td>{row.definition ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
